import {
  Avatar,
  Badge,
  Box,
  ProgressCircle,
  Separator,
  HStack,
  Icon,
  Text,
  VStack,
  Button,
  Wrap,
  Flex,
  Stack,
  AbsoluteCenter,
} from '@chakra-ui/react'
import { AnimatedSection, useAnimationContext } from '@src/components/sco/AnimatedSection'
import { useDelayedText } from '@src/components/sco/TypingText'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { LuCheck } from 'react-icons/lu'

type InvestorData = {
  id: number
  name: string
  title: string
  location: string
  matches: string[]
  matchScore: number
  avatarUrl: string
}

type InvestorMatchExplanationEffect = 'none' | 'typing' | 'fade' | 'collapse'

const InvestorProfileCardAnim = () => {
  const [showInputs, setShowInputs] = useState(true)
  const [showResults, setShowResults] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowInputs(false)
      setTimeout(() => {
        setShowResults(true)
      }, 500)
    }, 9000)

    return () => clearTimeout(timer)
  }, [])

  return (
    <AnimatedSection
      maxWidth={'600px'}
      width={'100%'}
      variants={{
        hidden: { opacity: 0, x: 20 },
        visible: {
          opacity: 1,
          x: 0,
          transition: {
            duration: 0.6,
            ease: [0.34, 1.56, 0.64, 1],
            delay: 0.2,
          },
        },
      }}
    >
      <InvestorCard />
    </AnimatedSection>
  )
}

const InvestorCard = () => {
  const { isVisible } = useAnimationContext()
  const investors: InvestorData[] = [
    {
      id: 1,
      name: 'Rick Thompson',
      title: 'Angel (Individual)',
      location: 'USA',
      matches: [
        'Entertainment',
        'Social Media',
        'Mobile Apps',
        'Seed',
        'SaaS',
        'Marketing',
        'Venture Capital',
      ],
      matchScore: 93,
      avatarUrl:
        'https://scoaccounts.s3.us-east-2.amazonaws.com/airtable/attKCA1xe6Q7LWypk/yedmoedudeflsexevd1g.webp',
    },
  ]

  const matchExplanation =
    "Rick Thompson should be interested in Superfan because he is an angel investor who invests in the seed stage. Superfan's focus on fan engagement and the music industry aligns well with Rick's interest in entertainment, social media, and mobile apps. Given Rick's history with companies like Playdom and Funzio, Superfan's focus on app development and content could be a great fit."
  const delayedMatchExplanation = useDelayedText(matchExplanation, 100, isVisible)

  return (
    <VStack gap={4} width={'100%'}>
      {investors.map((investor, index) => (
        <motion.div
          key={investor.id}
          animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          initial={{ opacity: 0, scale: 0.95 }}
          transition={{
            duration: 0.6,
            delay: 0.2 * index,
            ease: [0.34, 1.56, 0.64, 1],
          }}
        >
          <HStack
            bgColor={'colorPalette.container'}
            borderRadius={'xl'}
            boxShadow={'md'}
            gap={10}
            padding={6}
            position={'relative'}
            width={'full'}
          >
            <VStack align={'flex-start'} gap={6} justify={'center'} width={'full'}>
              <Stack direction={'row'} gap={4} width={'full'}>
                <VStack align={'flex-start'} gap={4} justify={'center'} width={'full'}>
                  <HStack gap={4}>
                    <Box position={'relative'}>
                      <Avatar.Root borderRadius={'lg'} size={'lg'}>
                        <Avatar.Fallback borderRadius={'lg'} />
                        <Avatar.Image borderRadius={'lg'} src={investor.avatarUrl} />
                      </Avatar.Root>
                      <Flex
                        align={'center'}
                        bgColor={'green'}
                        borderRadius={'full'}
                        bottom={'-3px'}
                        boxShadow={'md'}
                        height={'20px'}
                        justify={'center'}
                        position={'absolute'}
                        right={'-3px'}
                        width={'20px'}
                      >
                        <Icon asChild color={'white'}>
                          <LuCheck />
                        </Icon>
                      </Flex>
                    </Box>

                    <VStack align={'flex-start'} gap={0} justify={'flex-start'} padding={0}>
                      <Text color={'colorPalette.fg'} fontWeight={'semibold'} textStyle={'md'}>
                        {investor.name}
                      </Text>
                      <HStack gap={2}>
                        <Text color={'colorPalette.fg/70'} textStyle={'sm'}>
                          {investor.title}
                        </Text>
                        <Separator color={'colorPalette.border'} height={'12px'} orientation={'vertical'} />
                        <Text color={'colorPalette.fg/70'} textStyle={'sm'}>
                          {investor.location}
                        </Text>
                      </HStack>
                    </VStack>
                  </HStack>
                  <VStack align={'flex-start'} gap={2} justify={'flex-start'} width={'full'}>
                    <Text color={'colorPalette.fg'} textStyle={'label2xs'}>
                      Matches:
                    </Text>
                    <Wrap align={'center'} gap={1} justify={'flex-start'} width={'full'}>
                      {investor.matches.map((match, idx) => (
                        <Badge
                          key={idx}
                          bgColor={'colorPalette.card'}
                          borderRadius={'sm'}
                          color={'colorPalette.fg'}
                          textStyle={'2xs'}
                          variant={'subtle'}
                        >
                          {match}
                        </Badge>
                      ))}
                    </Wrap>
                  </VStack>
                </VStack>
                <VStack
                  align={'center'}
                  bgColor={'colorPalette.card'}
                  borderRadius={'lg'}
                  boxShadow={'sm'}
                  gap={4}
                  justify={'center'}
                  maxWidth={'180px'}
                  padding={4}
                  width={'full'}
                >
                  <ProgressCircle.Root colorPalette={'teal'} size={'2xl'} value={investor.matchScore}>
                    <ProgressCircle.Circle>
                      <ProgressCircle.Track />
                      <ProgressCircle.Range strokeLinecap={'round'} />
                    </ProgressCircle.Circle>
                    <AbsoluteCenter>
                      <Text color={'colorPalette.fg'} textStyle={'sm'}>
                        {investor.matchScore}%
                      </Text>
                    </AbsoluteCenter>
                  </ProgressCircle.Root>
                  <Button size={'sm'} variant={'cta'} width={'full'}>
                    Pitch
                  </Button>
                </VStack>
              </Stack>
              <Separator width={'full'} />
              <VStack align={'flex-start'} gap={2} justify={'flex-start'} width={'full'}>
                <Text color={'colorPalette.fg'} textStyle={'label2xs'}>
                  Why you should pitch:
                </Text>
                <Box height={'auto'} width={'full'}>
                  <Text
                    color={'colorPalette.fg/70'}
                    fontFamily={'longform'}
                    lineHeight={'tall'}
                    textStyle={'xs'}
                  >
                    {delayedMatchExplanation}
                  </Text>
                </Box>
              </VStack>
            </VStack>
          </HStack>
        </motion.div>
      ))}
    </VStack>
  )
}

export default InvestorProfileCardAnim
